import { toast } from "react-toastify";
import { commonHeader, generateEndpoint } from "~/config/apiConfig";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ErrorBoundary } from "~/errors/errorBoundary";
import type { CommonGenerationBody, SummaryGenerationBody } from "~/interface/generate";

export class GenerateApi {
  private getRequestInfo(
    method: string,
    requestBody?: SummaryGenerationBody | CommonGenerationBody,
  ): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: false,
      ...{
        body: JSON.stringify(requestBody),
      },
    };
  }

  async getClientAccessUrl(organization_id: string): Promise<string | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/generate/client-access-url`,
    );
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const url = await response.json();
        return url;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateSummary(
    organization_id: string,
    project_id: string,
    body: SummaryGenerationBody,
  ): Promise<boolean> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate/summary`,
    );
    const requestInfo = this.getRequestInfo("POST", body);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return false;
        } else {
          const errorResponse = await response.json();
          const errorMessage = errorResponse.message;
          console.error(errorMessage);
          toast.error(errorMessage);
          return false;
        }
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async generateSystemProposal(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "system-proposal" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateEstimation(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "estimation" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateFunctionalRequirements(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "functional-requirements" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateNonFunctionalRequirements(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "non-functional-requirements" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateWorkflow(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "workflow" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generatePluginProposal(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "plugin-proposal" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateAppDesign(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "app-design" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateFieldDesign(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "field-design" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateEntityRelationship(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "entity-relationship" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }
  async generateSystemArchitecture(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "system-architecture" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async generateCustomizeSpecification(
    organization_id: string,
    project_id: string,
    body: CommonGenerationBody,
  ): Promise<number | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/generate`,
    );
    const requestBody = { ...body, type: "customize-specification" };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return 0;
      } else {
        if (response.status === 409) {
          toast.error(TOAST_MESSAGES.GENERATION.ALREADY_GENERATING);
          return null;
        } else {
          const errorMessage = ErrorBoundary(response.status);
          console.error(errorMessage);
          const data = await response.json();
          toast.error(data.message);
          return null;
        }
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
